<!-- @TODO - move to design system -->

<script lang="ts">
	import { Text, Icon, LiveIndicator } from '@Shelly/shelly-design-system';
	import { createEventDispatcher } from 'svelte';
	import { load_image_from } from 'src/helpers/interface_helpers';
	import { requestVideoFullscreen } from 'src/components/Camera/helpers/camera-helpers';
	import DashCameraWidgetHeader from './components/DashCameraWidgetHeader.svelte';
	import DashCameraOverlayActions from './components/DashCameraOverlayActions.svelte';
	import CameraTile from 'src/components/Camera/MonitoringDashboard/CameraTile.svelte';
	import type { Device } from 'src/types/Device';
	import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';
	import type { DeviceStatusG2 } from 'src/types/DeviceStatusG2';

	type AdditionalText = {
		value: string;
		icon?: string;
		type?: 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'muted';
	};

	let className = '';
	export { className as class };

	export let value: string;
	export let offline = false;
	export let additionalText: AdditionalText | undefined;
	export let isFocusable = false;
	export let selected = false;
	export let hideRightTitleSlot = false;
	export let hideMainSlot = false;

	export let previewImage = '';
	export let imageFit: 'cover' | 'contain' = 'cover';
	export let offlineIcon = 'fa-light fa-cloud-slash';

	const defaultPlaceholderSrc = load_image_from('images/device_images/logo_white_S.png');

	export let arrowRight = false;
	export let arrowRightIcon = 'fa-regular fa-ellipsis-vertical';
	export let active = false;
	export let editMode = false;
	export let a11yProps: { arrowButtonAriaLabel?: string } = {};

	export let device: Device | undefined;
	export let status: DeviceStatusG2 | undefined;
	export let settings: DeviceSettingsG2 | undefined;
	export let showLiveBadge = false;
	export let headerCompact = false;
	/** Smaller header controls for camera button card grid tiles. */
	export let denseOverlay = false;
	/** Cloud icon for online/offline instead of Live/Offline chip. */
	export let useCloudConnectionIcon = false;
	/** Show device name on the bottom-left only when the card is hovered or focused. */
	export let showNameOnHover = false;
	/** Mount live WebRTC player as full-bleed background instead of a snapshot image. */
	export let useLiveFeed = false;
	/** Red live dot at top-left when live feed is active. */
	export let showLiveIndicator = false;
	/** Place live dot in the top-right status row instead of top-left. */
	export let liveIndicatorAlignEnd = false;
	/** Show native-video fullscreen control (dashboard widgets & camera monitoring only). */
	export let showFullscreen = false;

	const dispatch = createEventDispatcher();

	$: showStatusHeader = !!device;

	$: showPreview = !!previewImage;
	$: useLiveFeedBackground = !editMode && useLiveFeed && !!device && !offline;
	$: hasCoverBackground =
		showPreview ||
		useLiveFeedBackground ||
		(!editMode && useLiveFeed && !!device && offline && showPreview);
	$: showAdditionalText =
		additionalText?.value && (!$$slots.main || hideMainSlot) && !(offline && useCloudConnectionIcon);
	// On the dark preview gradient we have to force white text/icons. Without the
	// preview the card looks exactly like a DS device card, so we let the Text /
	// Icon defaults take over (which is what DashDeviceWidget itself does).
	$: onPreviewClass = hasCoverBackground ? 'text-white' : '';
	$: additionalTextClass = additionalText?.type ? `text-${additionalText.type}` : onPreviewClass;
	$: hasSupplementaryInfo = showAdditionalText || (!!$$slots.main && !hideMainSlot);

	let rootEl: HTMLDivElement;

	function handleKeydown(e: KeyboardEvent) {
		if (!isFocusable) return;
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			rootEl?.click();
		}
	}

	function handleArrowClick(e: CustomEvent<MouseEvent>) {
		e.detail?.stopPropagation();
		dispatch('arrow-click', e.detail ?? e);
	}

	function handleFullscreenClick(e: CustomEvent<MouseEvent>) {
		e.detail?.stopPropagation();
		const videoElement = rootEl?.querySelector('video') as HTMLVideoElement | null;
		if (requestVideoFullscreen(videoElement)) return;
		dispatch('fullscreen', e.detail ?? e);
	}

	$: showTopMenu = arrowRight && !editMode && showStatusHeader;
	$: showFullscreenButton = showFullscreen && !editMode && !offline && showStatusHeader;

	function handleImageError() {
		previewImage = '';
	}
</script>

<!-- svelte-ignore a11y-no-noninteractive-tabindex -->
<div
	bind:this={rootEl}
	class="dash-camera-device-card {className}"
	class:is-focusable={isFocusable && !editMode}
	class:offline
	class:selected
	class:active={active && !offline && !editMode}
	class:dense-overlay={denseOverlay}
	class:show-name-on-hover={showNameOnHover}
	class:has-cover={hasCoverBackground}
	role={isFocusable ? 'button' : 'group'}
	tabindex={isFocusable && !editMode ? 0 : -1}
	{...$$restProps}
	on:click
	on:keydown={handleKeydown}
	on:drag
	on:dragenter
	on:dragleave
	on:dragover
	on:dragstart
	on:drop
>
	<div class="background">
		{#if useLiveFeedBackground && device}
			<div class="live-background">
				<CameraTile
					camera={device}
					editMode={false}
					mediaFit={imageFit}
					embedMode
					backgroundOnly
					fillContainer
				/>
			</div>
			<div class="gradient-overlay"></div>
		{:else if showPreview}
			<img
				class="preview"
				src={previewImage}
				alt={value}
				style={`object-fit: ${imageFit};`}
				on:error={handleImageError}
				draggable="false"
			/>
			{#if !editMode}
				<div class="gradient-overlay"></div>
			{/if}
		{:else}
			<img class="preview-placeholder" src={defaultPlaceholderSrc} alt="" draggable="false" />
		{/if}
	</div>

	{#if showLiveIndicator && !liveIndicatorAlignEnd && !editMode}
		<div class="camera-live-indicator" aria-hidden="true">
			<LiveIndicator />
		</div>
	{/if}

	{#if offline && !editMode && !showStatusHeader}
		<div class="offline-indicator">
			<Icon icon={offlineIcon} size="s" class={showPreview ? 'text-white' : 'text-color-muted'} />
		</div>
	{/if}

	{#if showStatusHeader && !editMode}
		<div class="status-header">
			<DashCameraWidgetHeader
				{device}
				{status}
				{settings}
				showLiveBadge={showLiveBadge && !showLiveIndicator}
				compact={headerCompact}
				{denseOverlay}
				{useCloudConnectionIcon}
			/>
			{#if showLiveIndicator && liveIndicatorAlignEnd}
				<div class="camera-live-indicator align-end" aria-hidden="true">
					<LiveIndicator />
				</div>
			{/if}
		</div>
	{/if}

	{#if (showTopMenu || showFullscreenButton) && !editMode}
		<div class="corner-actions">
			<DashCameraOverlayActions
				showMenu={showTopMenu}
				showFullscreen={showFullscreenButton}
				dense={denseOverlay}
				menuIcon={arrowRightIcon}
				on:menu-click={handleArrowClick}
				on:fullscreen={handleFullscreenClick}
			/>
		</div>
	{/if}

	{#if showNameOnHover && value && !editMode}
		<div class="hover-camera-name">
			<Text styleType="body-extra-small" class="text-white" noWrap>{value}</Text>
		</div>
	{/if}

	{#if !editMode}
		<div class="content">
			<div class="bottom-row">
				<div class="value-section-wrapper">
					{#if showAdditionalText}
						<div
							class="d-flex gap-xs align-items-center additional-text-row {additionalTextClass}"
						>
							{#if additionalText?.icon}
								<Icon size="xs" icon={additionalText.icon} />
							{/if}
							<Text styleType="body-extra-small" noWrap>{additionalText.value}</Text>
						</div>
					{/if}

					{#if $$slots.main && !hideMainSlot}
						<div class="main-slot">
							<slot name="main" />
						</div>
					{/if}

					<div class="value-section" class:hide-name={showNameOnHover}>
						<Text
							noWrap={hasSupplementaryInfo}
							class="flex-1 min-width-0 text-500 {onPreviewClass} {hasSupplementaryInfo
								? ''
								: 'ellipse-2-lines'}"
							styleType="body-small"
						>
							{value}
						</Text>
						{#if $$slots.rightTitle && !hideRightTitleSlot}
							<div class="right-title-slot">
								<slot name="rightTitle" />
							</div>
						{/if}
					</div>
				</div>
			</div>
		</div>
	{/if}
</div>

<style>
	.dash-camera-device-card {
		position: relative;
		display: flex;
		flex-direction: column;
		width: 100%;
		height: 100%;
		min-height: 114px;
		border-radius: var(--radius-big);
		overflow: hidden;
		background-color: var(--color-1);
		outline: none;
		--camera-overlay-inset: var(--spacing-s-plus);
	}

	.dash-camera-device-card.dense-overlay {
		--camera-overlay-inset: var(--spacing-s);
	}

	.dash-camera-device-card.offline {
		background-color: var(--color-0);
	}

	.dash-camera-device-card.active {
		background-color: var(--color-white);
	}

	.background {
		position: absolute;
		inset: 0;
		z-index: 0;
		pointer-events: none;
	}

	.preview,
	.preview-placeholder {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
		object-position: center;
		background-color: var(--color-0);
	}

	.live-background {
		position: absolute;
		inset: 0;
		overflow: hidden;
	}

	.live-background :global(.camera-tile-root) {
		width: 100%;
		height: 100%;
	}

	.live-background :global(.camera-tile-widget) {
		width: 100%;
		height: 100%;
		aspect-ratio: unset;
	}

	.live-background :global(.video-container-wrapper),
	.live-background :global(.video-player-area),
	.live-background :global(.zoom-container) {
		height: 100%;
	}

	.live-background :global(.video-player-area video),
	.live-background :global(.state-placeholder) {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	.camera-live-indicator {
		position: absolute;
		top: var(--camera-overlay-inset);
		left: var(--camera-overlay-inset);
		z-index: 4;
		pointer-events: none;
		line-height: 0;
	}

	.camera-live-indicator.align-end {
		position: static;
		flex-shrink: 0;
	}

	.preview {
		display: block;
		background-color: var(--color-0);
	}

	.dash-camera-device-card.offline .preview {
		filter: grayscale(100%);
		opacity: 0.6;
	}

	.status-header {
		position: absolute;
		top: 0;
		right: 0;
		left: 0;
		z-index: 3;
		display: flex;
		justify-content: flex-end;
		align-items: flex-start;
		gap: var(--spacing-xs);
		padding: var(--camera-overlay-inset);
		pointer-events: none;
	}

	.corner-actions {
		position: absolute;
		right: var(--camera-overlay-inset);
		bottom: var(--camera-overlay-inset);
		z-index: 3;
	}

	.offline-indicator {
		position: absolute;
		top: var(--spacing-s-plus);
		right: var(--spacing-s-plus);
		z-index: 2;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.gradient-overlay {
		position: absolute;
		inset: 0;
		background: linear-gradient(
			180deg,
			rgba(0, 0, 0, 0.35) 0%,
			rgba(0, 0, 0, 0.05) 28%,
			rgba(0, 0, 0, 0.05) 40%,
			rgba(0, 0, 0, 0.55) 64%,
			rgba(0, 0, 0, 0.9) 100%
		);
		pointer-events: none;
	}

	.content {
		position: relative;
		z-index: 1;
		display: flex;
		flex-direction: column;
		flex: 1;
		min-height: 0;
		padding: var(--spacing-s-plus);
		gap: var(--spacing-s-plus);
	}

	.bottom-row {
		display: flex;
		align-items: flex-end;
		margin-top: auto;
		position: relative;
	}

	.dash-camera-device-card.show-name-on-hover .content {
		padding: 0;
	}

	.dash-camera-device-card.show-name-on-hover .value-section.hide-name {
		display: none;
	}

	.hover-camera-name {
		position: absolute;
		left: calc(var(--camera-overlay-inset) + 15px);
		bottom: calc(var(--camera-overlay-inset) + 5px);
		z-index: 3;
		max-width: calc(100% - 40px);
		opacity: 0;
		pointer-events: none;
		transition: opacity 0.2s ease;
	}

	.dash-camera-device-card.show-name-on-hover:hover .hover-camera-name,
	.dash-camera-device-card.show-name-on-hover:focus-within .hover-camera-name {
		opacity: 1;
	}

	.value-section-wrapper {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs);
		flex: 1;
		min-width: 0;
	}

	.value-section {
		display: flex;
		align-items: flex-end;
		gap: var(--spacing-s);
		flex: 1;
		min-width: 0;
	}

	.value-section :global(.ellipse-2-lines) {
		display: -webkit-box;
		-webkit-line-clamp: 2;
		line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}

	.right-title-slot {
		min-width: 0;
	}

	.main-slot {
		min-width: 0;
	}

	.additional-text-row {
		min-width: 0;
	}

	.dash-camera-device-card.is-focusable {
		cursor: pointer;
	}

	.dash-camera-device-card.is-focusable:focus-visible:not(.selected) {
		box-shadow: 0 0 0 4px rgba(36, 145, 255, 0.3);
	}

	.dash-camera-device-card.is-focusable:focus-visible:not(.selected)::after {
		content: '';
		position: absolute;
		inset: 0;
		border: 2px solid var(--color-primary);
		border-radius: inherit;
		pointer-events: none;
		z-index: 3;
	}

	.dash-camera-device-card.selected::after {
		content: '';
		position: absolute;
		inset: 0;
		border: 2px solid var(--color-primary);
		border-radius: inherit;
		pointer-events: none;
		z-index: 3;
	}
</style>
